import { CLOSED_FILTER, OPEN_FILTER } from "./taskFilterLabels.ts";

/**
 * Which kind of empty a filter is showing, so each platform can draw its own
 * glyph for one set of words.
 *
 * The kind rather than the icon itself: lucide ships a separate package per
 * platform and the two components are not interchangeable, where the words must
 * not differ at all. They already did — web told a reader who had cleared their
 * open list that they were all caught up, and native told the same reader "No
 * tasks yet.", which reads as a lost list rather than a finished one.
 */
export type TaskEmptyKind = "caughtUp" | "noneClosed" | "otherFilter";

type TaskEmptyState = {
  kind: TaskEmptyKind;
  heading: string;
  line: string;
};

/**
 * The words for a filter holding nothing.
 *
 * Guard clauses rather than a chain of conditions in the markup: each case is
 * a different thing to say, not a variation on one sentence. Its own wording
 * per filter, because telling someone with a full list that they have no tasks
 * reads as a bug rather than as an empty slice of one.
 */
export function taskEmptyState(filterKey: string, anyTasks: boolean): TaskEmptyState {
  // Nothing anywhere, which is a new account rather than a cleared list. The
  // same glyph as caught up, because the state is the same — nothing needs
  // them — and the words say what a task is, since they have not seen one.
  if (!anyTasks) {
    return {
      kind: "caughtUp",
      heading: "Starting fresh",
      line: "Okthx turns things that need doing into tasks, works through what it can, and leaves you the decisions.",
    };
  }
  // The one worth arriving at. Open is what the screen is for, so reaching the
  // end of it is the result and not an absence: it is the only empty here that
  // congratulates, and the only one whose glyph carries the brand colour. It
  // still says more is coming, because the next task lands without being asked
  // for and a reader told only to take a break would wonder whether it would.
  if (filterKey === OPEN_FILTER) {
    return {
      kind: "caughtUp",
      heading: "You're all caught up!",
      line: "Grab a well-deserved break. Okthx will bring you new tasks as they come in.",
    };
  }
  if (filterKey === CLOSED_FILTER) {
    return {
      kind: "noneClosed",
      heading: "Nothing closed yet",
      line: "Tasks you finish or drop are kept here.",
    };
  }
  return {
    kind: "otherFilter",
    heading: "Nothing here right now",
    line: "Another filter has the rest of your tasks.",
  };
}
